import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { refreshTokenIfNeeded } from './tokenUtils';

interface PasswordResetScreenProps {
  currentUser: { id: number; securityRoles: string[] } | null;
  accessToken?: string | null;
}

function hasRole(securityRoles: string[] | undefined, role: string): boolean {
  if (!securityRoles || !Array.isArray(securityRoles)) return false;
  return securityRoles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

const PasswordResetScreen: React.FC<PasswordResetScreenProps> = ({ currentUser, accessToken }) => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Only allow if admin or self (case-insensitive role check)
  if (!currentUser || (!hasRole(currentUser.securityRoles, 'admin') && currentUser.id !== Number(memberId))) {
    return <div>You are not authorized to reset this password.</div>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const token = await refreshTokenIfNeeded(accessToken || null);
      const res = await fetch(`/api/members/${memberId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to reset password');
      setSuccess('Password reset successfully.');
      setTimeout(() => navigate('/'), 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded shadow">
      <h2 className="text-xl font-bold mb-4">Reset Password</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block mb-1">New Password</label>
          <input
            type="password"
            className="w-full border px-3 py-2 rounded"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1">Confirm Password</label>
          <input
            type="password"
            className="w-full border px-3 py-2 rounded"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
          />
        </div>
        {error && <div className="text-red-600 mb-2">{error}</div>}
        {success && <div className="text-green-600 mb-2">{success}</div>}
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded"
          disabled={loading}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>
    </div>
  );
};

export default PasswordResetScreen;
